<script>
  import { refreshAll } from '$app/navigation';
  import ButtonLabel from '#lib/ButtonLabel.svelte';
  import BlockGrid from '#lib/BlockGrid.svelte';
  import SubscriptionManagement from '#lib/SubscriptionManagement.svelte';
  import { fetch_json, format_date } from '#lib/util.js';
  import { format_price } from '#lib/patch_util.js';
  import * as env from '$app/env/public';
  import t from '#lib/i18n.svelte.js';
  import { getContext } from 'svelte';
  import { page } from '$app/state';
  import { goto } from '$app/navigation';
  import { toast } from '#lib/toast.svelte.js';
  import Dialog from '#lib/Dialog.svelte';
  import Alert from '#lib/Alert.svelte';
  import AccountSwitcher from '#lib/AccountSwitcher.svelte';

  let { data } = $props();
  let site_settings = getContext('site_settings');

  const CANCELLATION_NOTICE_PERIOD_OFFICE = parseInt(env.PUBLIC_CANCELLATION_NOTICE_PERIOD_OFFICE, 10);
  const CANCELLATION_NOTICE_PERIOD_FIXED_DESK = parseInt(env.PUBLIC_CANCELLATION_NOTICE_PERIOD_FIXED_DESK, 10);
  const CANCELLATION_NOTICE_PERIOD_ADDON = parseInt(env.PUBLIC_CANCELLATION_NOTICE_PERIOD_ADDON, 10);

  const notice_period = $derived.by(() => {
    if(data.resource.resource_type === 'office') {
      return CANCELLATION_NOTICE_PERIOD_OFFICE;
    } else if (data.resource.resource_type === 'fixed_desk') {
      return CANCELLATION_NOTICE_PERIOD_FIXED_DESK;
    } else if (data.resource.resource_type === 'addon') {
      return CANCELLATION_NOTICE_PERIOD_ADDON;
    }
    return 0;
  });

  let current_user_can_book = $derived(
    data.current_user?.role === 'admin' 
    // also admins when impersonating another user can make bookings on their behalf (bypassing the payment method check)
    || data.authenticated_user?.role === 'admin' 
    || (
      data.current_user?.has_stripe_payment_method === 1 
      // For now we don't require a billing address, but we might in the future
      // && data.current_user?.has_stripe_billing_address === 1
    )
  );

  function __parse_year_month(year_month) {
    if (!year_month) return [null, null];
    return [
      parseInt(year_month.split('-')[0], 10),
      parseInt(year_month.split('-')[1], 10)
    ]
  }

  async function book_subscription() {
    try {
      const start_date = start_month_select_element.value;
      const [start_year, start_month] = __parse_year_month(start_date);

      const response = await fetch_json('POST', '/api/book-subscription', {
        resource_id: data.resource.entry_id,
        resource_name: data.resource.title,
        start_year,
        start_month
      });

      if (response.error) {
        // Handle specific error codes before falling through to generic handling
        if (response.error === 'NO_PAYMENT_METHOD_SET') {
          toast.info(
            t('add_missing_payment_details', {href: '/account/billing'}),
            { auto_dismiss: true, show_close_icon: true }
          );
          return;
        }
        toast.error(t('error_generic_error'));
        throw new Error(response.error);
      }

      await refreshAll();
      goto(`/book/${data.slug}#manage`);
      toast.success(t('subscription_booked_successfully', {
        resource_name: data.resource.title,
        user_name: data.current_user.name
      }));
    } catch (error) {
      console.error('Error booking subscription:', error);
      const error_messages = {
        'not_authorized': t('error_not_authorized'),
        'subscription_not_available_for_start_period': t('error_subscription_not_available_for_start_period'),
        'database_error': t('error_database_error'),
        'default': t('error_unknown')
      };

      toast.error(error_messages[error.message] || error_messages.default);
    }
  }

  let start_month_select_element = $state();
  let selected_start_month = $state();
  let booking_dialog = $state();

  let can_book = $derived.by(() => {
    if (!selected_start_month) return null;
    return data.available_start_months.find(month => month.year + '-' + month.month === selected_start_month && month.is_available);
  });

  function open_booking_dialog() {
    if (!selected_start_month) return;
    booking_dialog = { mode: 'confirmation' };
  }

  async function confirm_booking() {
    booking_dialog = undefined;
    await book_subscription();
  }
</script>


<BlockGrid>

  <header class='flex-row justify-center px py p-6'>
    {#if data.active_and_future_subscriptions.length > 0 || data.expired_subscriptions.length > 0}
      <div class='flex-row flex-wrap gap-x g-4'>
        <a href={`/book/${data.slug}`} data-sveltekit-replacestate>
          <ButtonLabel variant='brand {page.url.hash !== '#manage' ? 'tab-selected' : 'tab'}'>{t('new_subscription')}</ButtonLabel>
        </a>
        <a href={`/book/${data.slug}#manage`} data-sveltekit-replacestate>
          <ButtonLabel variant='brand {page.url.hash === '#manage' ? 'tab-selected' : 'tab'}'>{t('manage_subscriptions')}</ButtonLabel>
        </a>
      </div>
    {/if}
  </header>
  
  {#if page.url.hash !== '#manage' || data.active_and_future_subscriptions.length === 0 && data.expired_subscriptions.length === 0}
    <main class='flex-column gap-y g-7 py p-6'>
      <div class='flex-column gap-y g-6'>
        <h1 class='text-preamble leading-trim'>{t('book_new_subscription')}</h1>
        <h1 class='text-heading1 leading-trim'>{data.resource.title}</h1>
      </div>
      <div class='flex-column gap-y g-8'>
        <p class='text-body leading-trim'>{format_price(data.pricing?.price)} / {t('month')} <span class='text-3'>({t('plus_vat')})</span>
        </p>

        {#if data.resource.preview_description}
        <!-- <hr class='my m-4' /> -->
        <p class='text-body leading-trim'>{data.resource.preview_description}</p>
        <!-- <hr class='my m-4' /> -->
        {/if}

        <div class='flex-column'>
          <p class='text-body text-3'>{t('subscription_autorenewal')}</p>
          <p class='text-body text-3'>{t('cancellation_notice_period')}: {notice_period}× {notice_period === 1 ? t('month') : t('months')} {t('at_end_of_month')}</p>
          {#if site_settings.terms_and_conditions_preview_ref}
            <p class='text-body text-3'>
                {@html t('notice_terms_and_conditions', {
                  terms_and_conditions: site_settings.terms_and_conditions_preview_ref
                })}
            </p>
          {/if}
        </div>

        <hr class='my m-2' />

        <div class='flex-column gap-y g-4'>        
          <label class='text-body' for='start_date'>{t('when_do_you_want_to_start')}</label>
          <select id='start_date' bind:this={start_month_select_element} bind:value={selected_start_month}>
            <option value="" disabled selected>{t('select_start_date')}</option>
            {#each data.available_start_months as { year, month, is_available }, index}
              <option value='{year}-{month}' disabled={!is_available} selected={index === 0 && is_available}>
                {format_date(`${year}-${month}-01`, 'MMMMY')}
              </option>
            {/each}
            <option disabled>_________</option>
            <option disabled>{t('no_fitting_month_found')}</option>
            <option disabled>{t('contact_us_for_custom_requests')}</option>
          </select>
        </div>


        <div class='flex-column gap-y g-4'>
          {#if data?.current_user}
            <label class='text-body'>{t('booking_on_invoice_of')}{data.account_switcher_users?.length >= 1 ? '?' : ':'}</label>
          {/if}
          {#key data?.current_user?.user_id}
            <AccountSwitcher container='dropdown' />
          {/key}
        </div>
      </div>

      <div class='flex-column gap-y g-4'>
        {#if selected_start_month && !can_book}
          <div class='text-error leading-trim'>{t('selected_month_is_fully_booked')}</div>
        {/if}

        <div class='flex-column gap-y g-4'>
          {#if data?.current_user}
            <button
              class='full-width call-to-action'
              onclick={open_booking_dialog}
              disabled={selected_start_month && !can_book}
            >
              <ButtonLabel variant='brand {site_settings.primary_control_surface}'>
                {t('continue')}
              </ButtonLabel>
            </button>
          {:else}
            <a href="/login?return_to=book/{data.resource.slug}-{data.resource.entry_id}">
              <ButtonLabel variant='brand {site_settings.primary_control_surface}'>{t('login_and_book')}</ButtonLabel>
              </a>
          {/if}
          <p class='text-caption text-3 leading-trim'>{t('business_customer_vat_notice')}</p>

          <hr class='my m-4'>

          <p class='text-body leading-trim'>
            {t('no_fitting_month_found')} 
            <a class='underline' href='/messages'>{t('contact_us_for_custom_requests')}</a>
          </p>

        </div>


      </div>
    </main>
  {:else if page.url.hash === '#manage' && (data.active_and_future_subscriptions.length > 0 || data.expired_subscriptions.length > 0)}
    <aside class='flex-column gap-y g-7 py p-6'>
      <SubscriptionManagement {data} />
    </aside>
  {/if}

  {#if booking_dialog?.mode === 'confirmation'}
    <Dialog class='wide border-md flex-column gap-y g-4' onclose={() => (booking_dialog = undefined)}>
      <div class='px py p-6 bg-2 flex-row justify-between items-center'>
        <h3 class='text-body'>{t('confirm_your_booking')}</h3>
        <button onclick={() => (booking_dialog = undefined)}>
          <ButtonLabel start_icon='close' variant='brand ghostly' />
        </button>
      </div>
      <div class='form-row text-control text-1 px p-6'>
        <p class='text-3'>{t('subscription')}</p>
        <p>{data.resource.title}</p>
      </div>
      <div class='form-row text-control text-1 px p-6'>
        <p class='text-3'>{t('price')}</p>
        <p>{format_price(data.pricing?.price)} / {t('month')} <span class='text-3'>({t('plus_vat')})</span></p>
      </div>
      <div class='form-row text-control text-1 px p-6'>
        <p class='text-3'>{t('booking_for')}</p>
        <p>{data.current_user.name}</p>
      </div>
      <div class='form-row text-control text-1 px p-6'>
        <p class='text-3'>{t('start')}</p>
        <p>{format_date(`${selected_start_month}-01`, 'MMMMY')}</p>
      </div>
      {#if !current_user_can_book}
        <div class='flex-row px py p-6'>
          {#if data.current_user.has_stripe_payment_method === 0}
            <Alert variant='brand' icon='warning'>
              <p class='text-caption'>{t('no_payment_method_set_required_for_booking')}
                <a class='underline' href='/account/billing'>{t('add_payment_details')}</a>
              </p>
            </Alert>
          {:else if data.current_user.has_stripe_billing_address === 0}
            <Alert variant='brand' icon='warning'>
              <p class='text-caption'>{@html t('add_missing_billing_address', {href: '/account/billing'})}
              </p>
            </Alert>
          {/if}
        </div>
      {/if}
      <div class='flex-row justify-between gap-x px py p-6'>
        <button onclick={() => (booking_dialog = undefined)}>
          <ButtonLabel variant='brand {site_settings.secondary_control_surface}'>{t('back')}</ButtonLabel>
        </button>
        <button class='full-width' onclick={confirm_booking} disabled={!current_user_can_book}>
          <ButtonLabel variant='brand {site_settings.primary_control_surface}'>{t('book_and_pay_now')}</ButtonLabel>
        </button>
      </div>
    </Dialog>
  {/if}

</BlockGrid>




<style lang='postcss'>
  header, main, aside {
    grid-column: var(--track-span-inner);
  }
</style>

